import { usePageableTable } from "../../../context/PageableTableContext";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { PageableTable } from "../../../components/table";
import { Link, useNavigate } from "react-router-dom";
import { DateTimeFormat } from "libraries/date-time-format/date-time-format";
import { PageableTableActions } from "../../../states/pageable-table";
import { useArtworkApprovalContextStore } from "../context/approvals/ArtworkApprovalsContext";
import { Predicates } from "../../../libraries/predicates/predicates";
import { ArtworkApprovalActions } from "../state/approvals";
import { ArtworkApprovalRequestTypes } from "../state/approvals/data.type";
import ArtworkPictureModal from "./ArtworkPicutureModal";
import { PrintingTechniqueActions } from "../state/printingTechnique/create";
import { CreatePrintingTechniqueTypes } from "../state/printingTechnique/create/data";
import ReactModal from "react-modal";

const ArtworkApprovalRequestsIdCellRenderer = memo(
  (model: { id: number | string }) => {
    return (
      <div className="d-flex">
        <span className="pe-2 flex-fill align-self-center">{model.id}</span>
        <div></div>
      </div>
    );
  },
);

interface ITableArtworkPrintingTechniqueRequestProps {
  printingTechnique: any;
  isPrintingTechniqueLoading: boolean;
}

export const ArtworkPrintingTechniqueSearchResultsPagableTable = () => {
  const navigate = useNavigate();

  const {state, dispatch } = useArtworkApprovalContextStore();
  const {printingTechniqueSearchResults: printingTechnique, printingTechniqueSearchResultsIsLoading:isLoading } = state

  const [showPictureModal, setShowPictureModal] = useState<boolean>(false);
  const [imageToModal, setImageToModal] = useState<any>({
    imageName:null,
    image_file: null,
  });


  const gridRef = useRef<AgGridReact<Array<any>>>(null);

  const {
    dispatch: dispatchPageTable,
    state: { offset },
  } = usePageableTable();

  useEffect(() => {
    dispatch(
      ArtworkApprovalActions.setStateValue(
        ArtworkApprovalRequestTypes.SET_OFFSET,
        offset,
      ),
    );
  }, [offset]);

  useEffect(() => {
    dispatchPageTable(
      PageableTableActions.setLoading(isLoading),
    );
    if (Predicates.isNotNullAndNotUndefined(printingTechnique)) {
      dispatchPageTable(
        PageableTableActions.setTotal(printingTechnique?.count),
      );
    }
  }, [printingTechnique, isLoading, dispatchPageTable]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Code / Name",
        field: "id",
        minWidth: 70,
        cellRenderer: (params: any) => {
          return (
            <>
              <ArtworkApprovalRequestsIdCellRenderer
                id={`${params?.data?.code} - ${params?.data?.name}`}
              />
            </>
          );
        },
      },
      {
        headerName: "Collection",
        field: "collection.id",
        minWidth: 20,
        maxWidth: 350,
        cellRenderer: (params: any) => {
          return (
            <ArtworkApprovalRequestsIdCellRenderer
              id={`${params.data?.collection?.id} - ${params.data?.collection?.name}`}
            />
          );
        },
      },
      {
        headerName: "Decoration Technique",
        field: "decoration_technique.id",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          return (
            <ArtworkApprovalRequestsIdCellRenderer
              id={`${params.data?.decoration_technique?.id} - ${params.data?.decoration_technique?.name}`}
            />
          );
        },
      },
      {
        headerName: "Color",
        field: "color.id ",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          return (
            <>{`${params.data?.color?.id} - ${params.data?.color?.name}`}</>
          );
        },
      },
      {
        headerName: "Molds",
        field: "data.status",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const molds = params.data?.molds;

          return (
            <>
              {molds?.length > 0 ? (
                molds.map((mold: any) => {
                  return (
                    <div style={{border:"1px solid #d4d4d4", borderRadius:"2px", background:"#e4e4e4", padding: "2px 4px", margin:"2px 0"}}>
                      {mold.id} - {mold.name}
                    </div>
                  );
                })
              ) : (
                <></>
              )}
            </>
          );
        },
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          return (
            <div className="d-flex flex-direction-column flex-wrap">
              <Link
                className="mt-1 me-3"
                to={`/artwork/printing-technique/detail/${params.data.unique_id}`}
                style={{ textDecoration: "underline" }}
                state={params.data}
              >
                View
              </Link>

              {params.data?.collection.image?.image_base64 &&
              <Link className="mt-1 me-3" to={""} onClick={() => {
                setImageToModal({
                  imageName: `Collection ${params.data?.collection.name ?? ""}`,
                  image_file: null,
                  image_base64: `data:image/jpeg;base64,${params.data?.collection.image?.image_base64}`,
                  isBlocked: true,
                });
                setShowPictureModal(true);
              }}>
                Artwork
              </Link>
              }

              {params.data?.picture.image_base64 &&
              <Link className="mt-1 me-3" to={""}  onClick={() => {
                setImageToModal({
                  imageName: `Technical File ${params.data?.name ?? ""}`,
                  image_file: null,
                  image_base64: `data:image/jpeg;base64,${params.data?.picture.image_base64}`,
                  isBlocked: true,
                });
                setShowPictureModal(true);
              }}>
                Technical File
              </Link>
              }

              <Link
                className="mt-1 me-3"
                to={`/product-master?no_clear=true`}
                state={{decorationTech: params.data.decoration_technique, design:params.data}}
                style={{ textDecoration: "underline", whiteSpace: "nowrap" }}
              >
                Show MPNs
              </Link>
            </div>
          );
        },
      },
    ],
    [printingTechnique],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      headerClass: "text-wrap",
      minWidth: 100,
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      suppressMenu: true,
      suppressMovable: true,
      sortable: true,
    }),
    [],
  );

  const tableProp = useMemo(
    () => ({
      gridRef,
      //@ts-ignore
      rowData: printingTechnique?.results || [],
      columnDefs,
      defaultColDef,
    }),
    [gridRef, printingTechnique, columnDefs, defaultColDef],
  );

  return (
    <>
      <PageableTable tableProp={{ ...tableProp }} />

      <ReactModal
        isOpen={showPictureModal}
        className="custom-modal mold-comps"
        overlayClassName="custom-overlay"
      >
        <ArtworkPictureModal
          handleCloseModal={() => setShowPictureModal(false)}
          product={imageToModal}
          setImage={() => {}}
        />
      </ReactModal>
    </>
  );
};
